// Authentication service for CareerCatalyst
// Handles login, logout, registration, and token refresh operations

import { api, tokenManager, startTokenRefreshTimer, stopTokenRefreshTimer } from './api';
import { resetPostHog } from '../lib/posthog';
import type { AccountType } from '../types/recruiter';

// Auth request/response types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  partner?: string;
  /** 'candidate' (default) or 'recruiter'. */
  account_type?: AccountType;
  /** Required when registering as a recruiter (creates the Agency). */
  agency_name?: string;
  /** Recruiter invite token for candidate signups from an invite link. */
  invite_token?: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  is_verified: boolean;
  is_admin: boolean;
  created_at: string;
  last_login_at: string | null;
  is_profile_complete: boolean;
  partner?: string | null;
  account_type: AccountType;
  /** Agency identifier (set iff the user is a recruiter). */
  agency_id: string | null;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface InviteValidationResponse {
  valid: boolean;
  email?: string;
  name?: string;
  expired: boolean;
}

export interface CompleteInviteRequest {
  token: string;
  password: string;
}

export interface VerificationResponse {
  message: string;
  user: UserProfile;
}

// OAuth Types
export type OAuthProvider = 'google' | 'apple' | 'linkedin' | 'microsoft' | 'github';

export interface SocialAccount {
  id: string;
  provider: OAuthProvider;
  provider_email: string | null;
  provider_name: string | null;
  provider_picture_url: string | null;
  is_primary: boolean;
  created_at: string;
  last_used_at: string | null;
}

export interface OAuthAuthorizeResponse {
  authorization_url: string;
  state: string;
  provider: string;
}

export interface OAuthLoginResponse {
  message: string;
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    user: UserProfile;
  };
  is_new_user: boolean;
  linked_account: SocialAccount;
}

export interface LinkedAccountsResponse {
  accounts: SocialAccount[];
  has_password: boolean;
}

export interface OAuthTokenRequest {
  provider: OAuthProvider;
  code?: string;
  id_token?: string;
  access_token?: string;
  redirect_uri?: string;
}

// AuthService class
class AuthService {
  /**
   * Login user with username and password
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await api.post<any>('/auth/login', credentials);

    // Backend returns tokens in a nested structure
    const authData: AuthResponse = response.tokens;

    // Store tokens
    tokenManager.setToken(authData.access_token);
    tokenManager.setRefreshToken(authData.refresh_token);

    // Start background token refresh
    startTokenRefreshTimer();

    return authData;
  }

  /**
   * Register new user
   */
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await api.post<any>('/auth/register', userData);

    // Backend returns tokens in a nested structure
    const authData: AuthResponse = response.tokens;

    // Store tokens
    tokenManager.setToken(authData.access_token);
    tokenManager.setRefreshToken(authData.refresh_token);

    // Start background token refresh
    startTokenRefreshTimer();

    return authData;
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<AuthResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });

    // Update stored tokens
    tokenManager.setToken(response.access_token);
    if (response.refresh_token) {
      tokenManager.setRefreshToken(response.refresh_token);
    }

    // Ensure refresh timer is running
    startTokenRefreshTimer();

    return response;
  }

  /**
   * Logout user (clear local tokens)
   */
  async logout(): Promise<void> {
    // Stop background token refresh
    stopTokenRefreshTimer();

    // Clear local tokens
    tokenManager.removeToken();
    tokenManager.removeRefreshToken();

    // Clear PostHog identity so the next user starts fresh.
    resetPostHog();

    // Note: Backend doesn't have a logout endpoint that invalidates tokens
    // Tokens will expire naturally or can be invalidated server-side if needed
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<UserProfile> {
    return api.get<UserProfile>('/auth/me');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!tokenManager.getToken();
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return tokenManager.getToken();
  }

  /**
   * Verify token validity (attempts to fetch current user)
   */
  async verifyToken(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validate invite token
   */
  async validateInvite(token: string): Promise<InviteValidationResponse> {
    return api.get<InviteValidationResponse>(`/auth/invite/${token}`);
  }

  /**
   * Complete invite-based registration
   */
  async completeInvite(request: CompleteInviteRequest): Promise<AuthResponse> {
    const response = await api.post<any>('/auth/complete-invite', request);

    // Backend returns tokens in a nested structure
    const authData: AuthResponse = response.tokens;

    // Store tokens
    tokenManager.setToken(authData.access_token);
    tokenManager.setRefreshToken(authData.refresh_token);

    // Start background token refresh
    startTokenRefreshTimer();

    return authData;
  }

  /**
   * Send verification email to current user
   */
  async sendVerificationEmail(): Promise<{ message: string }> {
    return api.post<{ message: string }>('/auth/send-verification', {});
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<VerificationResponse> {
    return api.get<VerificationResponse>(`/auth/verify-email/${token}`);
  }

  // =====================
  // OAuth Methods
  // =====================

  /**
   * Get list of configured OAuth providers
   */
  async getOAuthProviders(): Promise<OAuthProvider[]> {
    return api.get<OAuthProvider[]>('/auth/oauth/providers');
  }

  /**
   * Initiate OAuth flow - get authorization URL
   */
  async getOAuthAuthorizationUrl(
    provider: OAuthProvider,
    redirectUri?: string
  ): Promise<OAuthAuthorizeResponse> {
    return api.post<OAuthAuthorizeResponse>('/auth/oauth/authorize', {
      provider,
      redirect_uri: redirectUri,
    });
  }

  /**
   * Exchange OAuth credentials for tokens
   * Used for frontend-initiated flows (Google One Tap, etc.)
   */
  async loginWithOAuth(request: OAuthTokenRequest): Promise<OAuthLoginResponse> {
    const response = await api.post<OAuthLoginResponse>('/auth/oauth/token', request);

    // Store tokens
    tokenManager.setToken(response.tokens.access_token);
    tokenManager.setRefreshToken(response.tokens.refresh_token);

    // Start background token refresh
    startTokenRefreshTimer();

    return response;
  }

  /**
   * Login with Google ID token (from Google One Tap)
   */
  async loginWithGoogle(idToken: string): Promise<OAuthLoginResponse> {
    return this.loginWithOAuth({
      provider: 'google',
      id_token: idToken,
    });
  }

  /**
   * Login with Apple ID token
   */
  async loginWithApple(idToken: string): Promise<OAuthLoginResponse> {
    return this.loginWithOAuth({
      provider: 'apple',
      id_token: idToken,
    });
  }

  /**
   * Handle OAuth callback (for redirect-based flows)
   */
  async handleOAuthCallback(
    provider: OAuthProvider,
    code: string,
    state: string
  ): Promise<OAuthLoginResponse> {
    const response = await api.post<OAuthLoginResponse>(
      `/auth/oauth/callback/${provider}`,
      { code, state }
    );

    // Store tokens
    tokenManager.setToken(response.tokens.access_token);
    tokenManager.setRefreshToken(response.tokens.refresh_token);

    // Start background token refresh
    startTokenRefreshTimer();

    return response;
  }

  /**
   * Get all linked social accounts
   */
  async getLinkedAccounts(): Promise<LinkedAccountsResponse> {
    return api.get<LinkedAccountsResponse>('/auth/oauth/accounts');
  }

  /**
   * Link a new social account to current user
   */
  async linkSocialAccount(request: OAuthTokenRequest): Promise<{ message: string; linked_account: SocialAccount }> {
    return api.post('/auth/oauth/link', request);
  }

  /**
   * Unlink a social account from current user
   */
  async unlinkSocialAccount(provider: OAuthProvider): Promise<{ message: string; provider: string }> {
    return api.delete(`/auth/oauth/unlink/${provider}`);
  }

  /**
   * Get OAuth authorization URL for linking (includes user context)
   */
  async getOAuthLinkUrl(
    provider: OAuthProvider,
    redirectUri?: string
  ): Promise<OAuthAuthorizeResponse> {
    return api.post<OAuthAuthorizeResponse>('/auth/oauth/authorize/link', {
      provider,
      redirect_uri: redirectUri,
    });
  }
}

// Export singleton instance
export const authService = new AuthService();
